import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import type { NostrEvent } from '@nostrify/nostrify';

import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { PolaroidCard } from '@/components/PolaroidCard';
import { AlbumCoverCard } from '@/components/AlbumCoverCard';
import { HandwrittenNote } from '@/components/HandwrittenNote';
import { DateDoodle } from '@/components/DateDoodle';
import { Skeleton } from '@/components/ui/skeleton';
import { seedFromHex, formatPolaroidDate, getTapeVariant, getHashtags, getPastelStripColor } from '@/lib/polaroid-utils';
import { parseAlbum } from '@/lib/album-utils';
import { cn } from '@/lib/utils';

/**
 * Pick the first hashtag from an event that isn't the excluded tag.
 * Returns null if no suitable tag is found.
 */
function pickPrimaryTag(event: NostrEvent, excludeTag?: string): string | null {
  const hashtags = getHashtags(event);
  const normalizedExclude = excludeTag?.toLowerCase();
  for (const tag of hashtags) {
    if (normalizedExclude && tag.toLowerCase() === normalizedExclude) continue;
    return tag;
  }
  return null;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Get a YYYY-MM-DD key from a unix timestamp for day grouping */
function getDayKey(timestamp: number): string {
  const d = new Date(timestamp * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Track scroll direction globally for the feed */
function useScrollDirection() {
  const lastScrollY = useRef(0);
  const [direction, setDirection] = useState<'down' | 'up'>('down');

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setDirection(currentY > lastScrollY.current ? 'down' : 'up');
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return direction;
}

// ── Reveal animations ────────────────────────────────────────────────

/** Wrapper that pops a polaroid in when it scrolls into view */
function ScrollReveal({ children, index, scrollDirection }: { children: React.ReactNode; index: number; scrollDirection: 'down' | 'up' }) {
  const [hasRevealed, setHasRevealed] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.15,
    rootMargin: '0px 0px -40px 0px',
  });

  const shouldSnap = inView && scrollDirection === 'up' && !hasRevealed;

  useEffect(() => {
    if (inView && !hasRevealed) {
      setHasRevealed(true);
    }
  }, [inView, hasRevealed]);

  const isEven = index % 2 === 0;
  const isVisible = hasRevealed || inView;

  return (
    <div
      ref={ref}
      className={cn(
        'relative pointer-events-none',
        shouldSnap
          ? ''
          : 'transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
      )}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateY(0) translateX(0) scale(1)'
          : `translateY(30px) translateX(${isEven ? '-20px' : '20px'}) scale(0.92)`,
        zIndex: 5,
      }}
    >
      <div className="pointer-events-auto">
        {children}
      </div>
    </div>
  );
}

/**
 * Hook for embellishment reveal animation.
 * Returns a ref and whether the element is visible.
 * Triggers slightly earlier and animates faster than polaroid cards.
 */
function useEmbellishmentReveal(scrollDirection: 'down' | 'up') {
  const [hasRevealed, setHasRevealed] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.08,
    rootMargin: '0px 0px -20px 0px',
  });

  const shouldSnap = inView && scrollDirection === 'up' && !hasRevealed;

  useEffect(() => {
    if (inView && !hasRevealed) {
      setHasRevealed(true);
    }
  }, [inView, hasRevealed]);

  const isVisible = hasRevealed || inView;

  return { ref, isVisible, shouldSnap };
}

/**
 * Reveal wrapper for flow-positioned embellishments (e.g. inline date headers).
 * Only used for elements that participate in normal document flow.
 * NOT used for absolutely-positioned CardEmbellishments (those animate via CSS class).
 */
function EmbellishmentReveal({ children, scrollDirection, className, style: extraStyle }: { children: React.ReactNode; scrollDirection: 'down' | 'up'; className?: string; style?: React.CSSProperties }) {
  const { ref, isVisible, shouldSnap } = useEmbellishmentReveal(scrollDirection);

  return (
    <div
      ref={ref}
      className={cn(
        shouldSnap
          ? ''
          : 'transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        className,
      )}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
        ...extraStyle,
      }}
    >
      {children}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────

export function FeedSkeleton() {
  return (
    <div className="space-y-6 py-6">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'bg-polaroid-white rounded-[3px] p-3 pb-14 shadow-[0_3px_12px_rgba(45,27,78,0.08)]',
            i % 2 === 0 ? 'ml-0 mr-auto' : 'mr-0 ml-auto',
          )}
          style={{
            width: i % 2 === 0 ? '70%' : '60%',
            transform: `rotate(${(i % 3) - 1}deg)`,
          }}
        >
          <Skeleton className="aspect-[4/5] w-full skeleton-shimmer animate-shimmer" />
          <div className="mt-2 space-y-1">
            <Skeleton className="h-5 w-3/4 skeleton-shimmer animate-shimmer" />
            <Skeleton className="h-4 w-1/3 skeleton-shimmer animate-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Embellishments ───────────────────────────────────────────────────

/** CSS transition style for embellishment children */
const EMBELLISHMENT_TRANSITION = 'opacity 300ms cubic-bezier(0.16,1,0.3,1), transform 300ms cubic-bezier(0.16,1,0.3,1)';

/**
 * Background embellishments — positioned BEHIND the polaroid cards.
 * Date markers and handwritten notes in the margins.
 * Everything here sits at z-index 1-2, while cards are at z-index 5.
 * Each absolutely-positioned child animates individually via the `revealed` prop.
 */
function CardEmbellishments({ event, index, isEven, dateLabel, revealed, shouldSnap, hashtagInline, primaryTag, hasBgImage, onNoteClick, onTagClick, pastelColor, pastelDoodleColor }: {
  event: NostrEvent;
  index: number;
  isEven: boolean;
  /** If this is the first card of a new day, the formatted date string to display */
  dateLabel?: string;
  /** Whether the embellishments should be visible (animated in) */
  revealed: boolean;
  /** Whether to skip animation (snap immediately) */
  shouldSnap: boolean;
  /** If true, the hashtag is rendered inline above the card and should be skipped here */
  hashtagInline: boolean;
  /** The primary hashtag to display (already filtered for excludeTag) */
  primaryTag: string | null;
  /** Whether the active theme has a background image */
  hasBgImage: boolean;
  /** Called when the margin note is tapped */
  onNoteClick?: () => void;
  /** Called when a hashtag is tapped */
  onTagClick?: (tag: string) => void;
  /** Override pastel color for date/hashtag strips when no theme is active */
  pastelColor?: string;
  /** Override pastel color for standalone margin doodle backgrounds when no theme is active */
  pastelDoodleColor?: string;
}) {
  const eventId = event.id;
  // Show note when there's enough content to display as a margin note.
  const showNote = !!(event.content && event.content.trim().length >= 8);
  // If the hashtag is being rendered inline, don't show it in the margin
  const displayTag = hashtagInline ? null : primaryTag;

  // Decorations go in the opposite margin from the card
  const marginSide = isEven ? 'right' : 'left';

  // Per-item pastel colors — use override if provided, otherwise pick from event ID.
  // Different seed offsets so date, tag, and doodle each get their own color.
  const itemDateColor = !hasBgImage ? (pastelColor ?? getPastelStripColor(eventId + 'date')) : undefined;
  const itemTagColor = !hasBgImage ? (pastelColor ?? getPastelStripColor(eventId + 'htag')) : undefined;
  const itemDoodleColor = !hasBgImage ? (pastelDoodleColor ?? getPastelStripColor(eventId + 'dood')) : undefined;

  // Hashtag rotation — slight, like someone wrote it casually
  const tagRotation = displayTag ? ((seedFromHex(eventId, 40, 42) % 10) - 5) : 0;

  // Fade style — opacity-only animation to avoid conflicting with position transforms
  const fadeStyle: React.CSSProperties = shouldSnap
    ? { opacity: revealed ? 1 : 0 }
    : {
        opacity: revealed ? 1 : 0,
        transition: EMBELLISHMENT_TRANSITION,
      };

  return (
    <>
      {/* Sideways date marker — first card of each day, with a hand-drawn doodle */}
      {dateLabel && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: '50%',
            [marginSide]: '0%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            ...fadeStyle,
          }}
        >
          {/* Single background strip wraps both doodle and text — no gap */}
          <div
            className={cn('flex items-center gap-1', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
            style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              padding: '8px 4px',
              ...(itemDateColor ? { backgroundColor: itemDateColor } : {}),
            }}
          >
            {/* Doodle before date text when on right side — rotated to match sideways text */}
            {marginSide === 'right' && (
              <span style={{ display: 'inline-block', transform: 'rotate(90deg)' }}>
                <DateDoodle seed={eventId} size={26} />
              </span>
            )}
            <span
              className="font-hand font-bold text-pol-text whitespace-nowrap block"
              style={{
                fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              {dateLabel}
            </span>
            {/* Doodle after date text when on left side — rotated to match sideways text */}
            {marginSide === 'left' && (
              <span style={{ display: 'inline-block', transform: 'rotate(90deg)' }}>
                <DateDoodle seed={eventId} size={26} />
              </span>
            )}
          </div>
        </div>
      )}

      {/* Note + Hashtag in margin — positioned as a unit */}
      {(() => {
        // ── Estimate note dimensions ──
        // Card height estimate: polaroid cards are 65-75% of 480px wide, aspect 4:5 photo
        // + padding + caption ≈ 470-540px. Use 500px for % calculations.
        const CARD_H = 500;
        const words = showNote ? event.content.trim().split(/\s+/).length : 0;
        const chars = showNote ? event.content.trim().length : 0;
        const isShortNote = words <= 6;

        const estimatedLines = !showNote ? 0 : isShortNote
          ? Math.min(Math.ceil(Math.min(chars, 80) / 10), 3)
          : Math.min(Math.ceil(Math.min(chars, 220) / 10), 12);
        const noteHeightPx = !showNote ? 0 : (estimatedLines * 20) + 32 + 30; // lines×lh + padding + tape
        const noteHeightPct = (noteHeightPx / CARD_H) * 100;

        // Hashtag height as % (short tag ≈ 34px line + doodle + pastel strip padding)
        const tagHeightPx = displayTag ? 44 : 0;
        const tagHeightPct = (tagHeightPx / CARD_H) * 100;

        // ── Compute positions ──
        const safeTop = 20;
        const safeBottom = 90;
        const safeRange = safeBottom - safeTop;

        // Note is centered in the safe zone
        const noteGroupHeight = noteHeightPct;
        let noteTop = safeTop + (safeRange - noteGroupHeight) / 2;
        noteTop = Math.max(safeTop, Math.min(noteTop, safeBottom - noteGroupHeight));

        // When there's no note, center the tag in the safe zone.
        // (Short tags are hidden entirely when a note is present — see render condition.)
        const tagTop = safeTop + (safeRange - tagHeightPct) / 2;

        return (
          <>
            {/* Hashtag sideways between date and card — when both date label and tag are present */}
            {displayTag && dateLabel && !showNote && (() => {
              // Position between the date marker and the card edge.
              // The date sits at [marginSide]: 0%, so we inset the tag
              // toward the card, on the opposite side of the margin.
              const cardEdgeSide = marginSide === 'right' ? 'left' : 'right';
              const cardEdgePos = marginSide === 'right' ? '78%' : '70%';
              return (
                <div
                  className="absolute"
                  style={{
                    top: '50%',
                    [cardEdgeSide]: cardEdgePos,
                    transform: 'translateY(-50%)',
                    zIndex: 6,
                    ...fadeStyle,
                  }}
                >
                  <button
                    onClick={() => onTagClick?.(displayTag)}
                    className={cn('font-hand font-bold text-pol-text inline-flex items-center gap-1 whitespace-nowrap cursor-pointer hover:brightness-95 active:scale-95 transition-transform', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
                    style={{
                      fontSize: 'clamp(1.2rem, 4.5vw, 1.6rem)',
                      lineHeight: 1,
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      ...(itemTagColor ? { backgroundColor: itemTagColor } : {}),
                    }}
                  >
                    {displayTag}
                    <DateDoodle seed={eventId + 'tag'} size={18} />
                  </button>
                </div>
              );
            })()}

            {/* Long tag (vertical) — only shown when no note AND no date label */}
            {displayTag && displayTag.length > 8 && !showNote && !dateLabel && (() => {
              // Position near the card's opposite edge — pulled slightly
              // inward so the pastel strip padding doesn't clip at the
              // container boundary.
              const cardEdgeSide = marginSide === 'right' ? 'left' : 'right';
              const cardEdgePos = marginSide === 'right' ? '78%' : '70%';
              return (
                <div
                  className="absolute"
                  style={{
                    top: '50%',
                    [cardEdgeSide]: cardEdgePos,
                    transform: 'translateY(-50%)',
                    zIndex: 6,
                    ...fadeStyle,
                  }}
                >
                  <button
                    onClick={() => onTagClick?.(displayTag)}
                    className={cn('font-hand font-bold text-pol-text inline-flex items-center gap-1 whitespace-nowrap cursor-pointer hover:brightness-95 active:scale-95 transition-transform', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
                    style={{
                      fontSize: 'clamp(1.6rem, 6vw, 2.2rem)',
                      lineHeight: 1,
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      ...(itemTagColor ? { backgroundColor: itemTagColor } : {}),
                    }}
                  >
                    {displayTag}
                    <DateDoodle seed={eventId + 'tag'} size={24} />
                  </button>
                </div>
              );
            })()}

            {/* Short tag in margin — hidden when a note or date label is present to avoid overlap */}
            {displayTag && displayTag.length <= 8 && !showNote && !dateLabel && (
              <div
                className="absolute"
                style={{
                  top: `${tagTop}%`,
                  [marginSide]: '2%',
                  zIndex: 6,
                  ...fadeStyle,
                }}
              >
                <button
                  onClick={() => onTagClick?.(displayTag)}
                  className={cn('font-hand font-bold text-pol-text inline-flex items-center gap-1 whitespace-nowrap cursor-pointer hover:brightness-95 active:scale-95 transition-transform', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
                  style={{
                    fontSize: 'clamp(1.3rem, 5vw, 1.8rem)',
                    lineHeight: 1,
                    transform: `rotate(${tagRotation}deg)`,
                    ...(itemTagColor ? { backgroundColor: itemTagColor } : {}),
                  }}
                >
                  {displayTag}
                  <DateDoodle seed={eventId + 'tag'} size={22} />
                </button>
              </div>
            )}

            {/* Note — z-index 6 so it paints above the ScrollReveal card (z-5) */}
            {showNote && (
              <div
                className="absolute"
                style={{
                  top: `${noteTop}%`,
                  [marginSide]: marginSide === 'right' ? '-10%' : '-4%',
                  width: marginSide === 'right' ? '38%' : '40%',
                  zIndex: 6,
                  ...fadeStyle,
                }}
              >
                <HandwrittenNote event={event} side={marginSide === 'right' ? 'right' : 'left'} onClick={onNoteClick} />
              </div>
            )}
          </>
        );
      })()}

      {/* No caption AND no date AND no hashtag — show a random doodle in the margin.
       * Skip if there's already a date marker, note, or hashtag occupying the margin.
       * z-index 6 so it paints and clicks above the ScrollReveal card (z-5). */}
      {!showNote && !dateLabel && !displayTag && (
        <div
          className="absolute"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            [marginSide]: marginSide === 'right' ? '2%' : '6%',
            zIndex: 6,
            ...fadeStyle,
          }}
        >
          <DateDoodle
            seed={eventId + 'margin'}
            size={44 + (seedFromHex(eventId, 28, 30) % 20)}
            paperClass={hasBgImage ? 'paper-dot' : 'paper-dot-pastel'}
            paperColor={itemDoodleColor}
          />
        </div>
      )}
    </>
  );
}

// ── Feed card item ───────────────────────────────────────────────────

/** Individual feed card — extracted to its own component so hooks can be used per-card */
function FeedCardItem({ event, index, pictures, scrollDirection, onPhotoClick, excludeTag, onTagClick, pastelColor, pastelDoodleColor }: {
  event: NostrEvent;
  index: number;
  pictures: NostrEvent[];
  scrollDirection: 'down' | 'up';
  onPhotoClick: (event: NostrEvent) => void;
  excludeTag?: string;
  onTagClick: (tag: string) => void;
  pastelColor?: string;
  pastelDoodleColor?: string;
}) {
  const { ref: embellishRef, isVisible: embellishRevealed, shouldSnap: embellishSnap } = useEmbellishmentReveal(scrollDirection);
  const { hasBgImage } = useDittoThemeContext();

  const isEven = index % 2 === 0;
  const widthPercent = isEven ? 75 : 65;

  const overlapPx = index === 0 ? 0 : (4 + seedFromHex(event.id, 16, 18) % 10);
  const marginTopPx = index === 0
    ? 0
    : isEven
      ? 12 + overlapPx
      : -overlapPx;

  const dayKey = getDayKey(event.created_at);
  const prevDayKey = index > 0 ? getDayKey(pictures[index - 1].created_at) : null;
  const isFirstOfDay = index === 0 || dayKey !== prevDayKey;
  const formattedDate = isFirstOfDay ? formatPolaroidDate(event.created_at) : undefined;

  const cardHasContent = !!(event.content && event.content.trim().length >= 8);
  const showDateInline = formattedDate && cardHasContent;
  const dateLabel = formattedDate && !cardHasContent ? formattedDate : undefined;

  const prevTapeVariant = index > 0 ? getTapeVariant(pictures[index - 1].id) : undefined;

  // Determine if the hashtag should be rendered inline (below the card)
  // instead of in the margin. Only long hashtags get promoted to inline,
  // and only when there's a note competing for margin space.
  const primaryTag = pickPrimaryTag(event, excludeTag);
  const hasLongTag = !!primaryTag && primaryTag.length > 8;
  const contentLength = event.content ? event.content.trim().length : 0;
  const hasLongNote = contentLength > 60;
  const hashtagInline = !!primaryTag && hasLongTag && cardHasContent && !hasLongNote;

  // Hashtag rotation for inline display
  const tagRotation = primaryTag ? ((seedFromHex(event.id, 40, 42) % 6) - 3) : 0;

  // Per-item pastel colors for the inline elements in FeedCardItem
  const inlineDateColor = !hasBgImage ? (pastelColor ?? getPastelStripColor(event.id + 'idat')) : undefined;
  const inlineTagColor = !hasBgImage ? (pastelColor ?? getPastelStripColor(event.id + 'itag')) : undefined;

  return (
    <div
      ref={embellishRef}
      className="relative isolate"
      style={{ marginTop: `${marginTopPx}px` }}
    >
      {/* Horizontal date header — shown when the card has a caption note. */}
      {showDateInline && (
        <EmbellishmentReveal scrollDirection={scrollDirection} className="relative" style={{ zIndex: 10 }}>
          <div
            className={cn('flex items-center gap-2 px-2 pt-8 pb-10', hasBgImage ? 'paper-strip self-start' : 'paper-strip-pastel self-start')}
            style={inlineDateColor ? { backgroundColor: inlineDateColor } : undefined}
          >
            <DateDoodle seed={event.id} size={22} />
            <span
              className="font-hand font-bold text-pol-text whitespace-nowrap"
              style={{
                fontSize: 'clamp(1.8rem, 6vw, 2.4rem)',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              {formattedDate}
            </span>
            <DateDoodle seed={event.id + 'end'} size={20} />
          </div>
        </EmbellishmentReveal>
      )}

      {/* Background layer — embellishments BEHIND the polaroid */}
      <CardEmbellishments
        event={event}
        index={index}
        isEven={isEven}
        dateLabel={dateLabel}
        revealed={embellishRevealed}
        shouldSnap={embellishSnap}
        hashtagInline={hashtagInline}
        primaryTag={primaryTag}
        hasBgImage={hasBgImage}
        onNoteClick={() => onPhotoClick(event)}
        onTagClick={onTagClick}
        pastelColor={pastelColor}
        pastelDoodleColor={pastelDoodleColor}
      />

      {/* Foreground layer — polaroid card ON TOP */}
      <ScrollReveal index={index} scrollDirection={scrollDirection}>
        <div
          className={cn(
            isEven ? 'mr-auto' : 'ml-auto',
          )}
          style={{ width: `${widthPercent}%` }}
        >
          <PolaroidCard
            event={event}
            variant="feed"
            index={index}
            hideDate
            avoidTapeVariant={prevTapeVariant}
            onPhotoClick={onPhotoClick}
          />
        </div>
      </ScrollReveal>

      {/* Inline hashtag — below the card when margin would be too crowded */}
      {hashtagInline && primaryTag && (() => {
        return (
          <EmbellishmentReveal scrollDirection={scrollDirection}>
            <div
              className="flex items-center gap-1.5 px-2 pt-2"
              style={{ transform: `rotate(${tagRotation}deg)`, transformOrigin: 'left center' }}
            >
              <button
                onClick={() => onTagClick(primaryTag)}
                className={cn('font-hand font-bold text-pol-text inline-flex items-center gap-1 whitespace-nowrap cursor-pointer hover:brightness-95 active:scale-95 transition-transform', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}
                style={{
                  fontSize: 'clamp(1.4rem, 5vw, 1.8rem)',
                  lineHeight: 1,
                  ...(inlineTagColor ? { backgroundColor: inlineTagColor } : {}),
                }}
              >
                {primaryTag}
                <DateDoodle seed={event.id + 'tag'} size={20} />
              </button>
            </div>
          </EmbellishmentReveal>
        );
      })()}
    </div>
  );
}

// ── Main PolaroidFeed component ──────────────────────────────────────

interface PolaroidFeedProps {
  /** Flat list of picture events to render */
  pictures: NostrEvent[];
  /** Whether initial data is still loading */
  isLoading: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Whether a new page is currently being fetched */
  isFetchingNextPage: boolean;
  /** Callback to load the next page */
  fetchNextPage: () => void;
  /** Total number of loaded pages (used to gate infinite scroll) */
  pageCount: number;
  /** Callback when a photo is tapped — opens the photo detail overlay */
  onPhotoClick: (event: NostrEvent) => void;
  /** Optional empty state element to render when there are no pictures */
  emptyState?: React.ReactNode;
  /** Whether to show the "Vibed with Shakespeare" footer */
  showFooter?: boolean;
  /** Hashtag to exclude from margin embellishments (e.g. when viewing a hashtag feed, skip that tag) */
  excludeTag?: string;
  /** Override pastel color for date headers / hashtag strips when no theme is active */
  pastelColor?: string;
  /** Override pastel color for standalone margin doodle backgrounds when no theme is active */
  pastelDoodleColor?: string;
}

export function PolaroidFeed({
  pictures,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  pageCount,
  onPhotoClick,
  emptyState,
  showFooter = false,
  excludeTag,
  pastelColor,
  pastelDoodleColor,
}: PolaroidFeedProps) {
  const scrollDirection = useScrollDirection();
  const navigate = useNavigate();

  const handleTagClick = (tag: string) => {
    navigate(`/tag/${encodeURIComponent(tag)}`);
  };

  // Infinite scroll sentinel — track the page count so we only fetch
  // once per scroll, waiting for new data before allowing another fetch.
  const lastFetchedPage = useRef(0);
  const { ref: sentinelRef, inView: sentinelInView } = useInView();

  useEffect(() => {
    // Reset gate when new data arrives
    lastFetchedPage.current = pageCount;
  }, [pageCount]);

  useEffect(() => {
    if (
      sentinelInView &&
      hasNextPage &&
      !isFetchingNextPage &&
      lastFetchedPage.current === pageCount // only fire once per page load
    ) {
      lastFetchedPage.current = pageCount + 1; // lock gate until new data arrives
      fetchNextPage();
    }
  }, [sentinelInView, hasNextPage, isFetchingNextPage, fetchNextPage, pageCount]);

  if (isLoading) {
    return <FeedSkeleton />;
  }

  if (!pictures.length) {
    return emptyState ? <>{emptyState}</> : null;
  }

  return (
    <div className="py-6 space-y-0">
      {pictures.map((event, index) => {
        // Album events render as album cover cards
        if (event.kind === 30006) {
          const album = parseAlbum(event);
          if (!album) return null;

          const isEven = index % 2 === 0;
          const widthPercent = isEven ? 75 : 65;
          const overlapPx = index === 0 ? 0 : (4 + seedFromHex(event.id, 16, 18) % 10);
          const marginTopPx = index === 0
            ? 0
            : isEven
              ? 12 + overlapPx
              : -overlapPx;

          return (
            <div
              key={event.id}
              className="relative isolate"
              style={{ marginTop: `${marginTopPx}px` }}
            >
              <ScrollReveal index={index} scrollDirection={scrollDirection}>
                <div
                  className={cn(
                    isEven ? 'mr-auto' : 'ml-auto',
                  )}
                  style={{ width: `${widthPercent}%` }}
                >
                  <AlbumCoverCard album={album} variant="feed" index={index} />
                </div>
              </ScrollReveal>
            </div>
          );
        }

        // Regular photo events render as polaroid cards
        return (
          <FeedCardItem
            key={event.id}
            event={event}
            index={index}
            pictures={pictures}
            scrollDirection={scrollDirection}
            onPhotoClick={onPhotoClick}
            excludeTag={excludeTag}
            onTagClick={handleTagClick}
            pastelColor={pastelColor}
            pastelDoodleColor={pastelDoodleColor}
          />
        );
      })}

      {/* Infinite scroll sentinel */}
      {hasNextPage && (
        <div ref={sentinelRef} className="h-px" />
      )}
      {isFetchingNextPage && <FeedSkeleton />}

      {/* Vibed with Shakespeare */}
      {showFooter && (
        <div className="text-center pt-8 pb-4">
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pol-text-secondary/40 hover:text-pol-text-secondary transition-colors font-hand text-base"
          >
            Vibed with Shakespeare
          </a>
        </div>
      )}
    </div>
  );
}
